/**
 * Permission Service
 * Handles permission checking and user access control
 */

import { config } from "../config";

// API base URL from config (config.api.baseUrl already includes /api/v1 if needed)
const API_BASE_URL = config.api.baseUrl;

export interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PermissionCheckRequest {
  permission: string;
}

export interface PermissionCheckResponse {
  has_permission: boolean;
}

export interface UserPermissionsResponse {
  user_id: string;
  role: string;
  package_name?: string;
  permissions: Permission[];
}

export interface BusinessCreationStatus {
  can_create: boolean;
  has_permission: boolean;
  reason?: string;
}

/**
 * Check if the current user has a specific permission
 */
export const checkPermission = async (permission: string): Promise<boolean> => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(`${API_BASE_URL}/permissions/check`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ permission }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to check permission with status ${response.status}`
      );
    }

    const result: PermissionCheckResponse = await response.json();
    return result.has_permission;
  } catch (error) {
    console.error("❌ Error checking permission:", error);
    return false;
  }
};

/**
 * Get all permissions for the current user
 */
export const getUserPermissions =
  async (): Promise<UserPermissionsResponse | null> => {
    try {
      const token = localStorage.getItem("auth_token");

      const response = await fetch(`${API_BASE_URL}/permissions`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            `Failed to get user permissions with status ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("❌ Error getting user permissions:", error);
      return null;
    }
  };

/**
 * Get business creation status for the current user
 */
export const getBusinessCreationStatus =
  async (): Promise<BusinessCreationStatus | null> => {
    console.log("🔍 [permissionService] getBusinessCreationStatus called");
    try {
      const token = localStorage.getItem("auth_token");
      console.log(
        "🔑 [permissionService] Retrieved authToken:",
        token ? "present" : "null"
      );

      console.log(
        "🌐 [permissionService] Fetching business creation status from:",
        `${API_BASE_URL}/permissions/business-creation-status`
      );
      const response = await fetch(
        `${API_BASE_URL}/permissions/business-creation-status`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      console.log("📡 [permissionService] Response status:", response.status);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("❌ [permissionService] Response error data:", errorData);
        throw new Error(
          errorData.error ||
            `Failed to get business creation status with status ${response.status}`
        );
      }

      const result = await response.json();
      console.log("✅ [permissionService] Response data:", result);
      return result;
    } catch (error) {
      console.error(
        "❌ [permissionService] Error getting business creation status:",
        error
      );
      return null;
    }
  };

/**
 * Get all permissions (admin only)
 */
export const getAllPermissions = async (): Promise<Permission[]> => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(`${API_BASE_URL}/admin/permissions`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to get all permissions with status ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("❌ Error getting all permissions:", error);
    return [];
  }
};

/**
 * Get permissions for a specific role (admin only)
 */
export const getRolePermissions = async (
  role: string
): Promise<Permission[]> => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(
      `${API_BASE_URL}/admin/permissions/roles/${role}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to get role permissions with status ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("❌ Error getting role permissions:", error);
    return [];
  }
};

/**
 * Update permissions for a specific role (admin only)
 */
export const updateRolePermissions = async (
  role: string,
  permissionNames: string[]
): Promise<{ message: string }> => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(
      `${API_BASE_URL}/admin/permissions/roles/${role}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(permissionNames),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to update role permissions with status ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("❌ Error updating role permissions:", error);
    throw error;
  }
};

/**
 * Permission hooks for React components
 */
export const usePermissions = () => {
  const checkUserPermission = async (permission: string): Promise<boolean> => {
    return await checkPermission(permission);
  };

  const canCreateBusiness = async (): Promise<boolean> => {
    const status = await getBusinessCreationStatus();
    return status?.can_create || false;
  };

  const canViewBusinessMenu = async (): Promise<boolean> => {
    return await checkPermission("view_business_menu");
  };

  const canEditBusiness = async (): Promise<boolean> => {
    return await checkPermission("edit_business");
  };

  const canDeleteBusiness = async (): Promise<boolean> => {
    return await checkPermission("delete_business");
  };

  const canManageBusinessServices = async (): Promise<boolean> => {
    return await checkPermission("manage_business_services");
  };

  const canManageBusinessEmployees = async (): Promise<boolean> => {
    return await checkPermission("manage_business_employees");
  };

  const canViewBusinessAnalytics = async (): Promise<boolean> => {
    return await checkPermission("view_business_analytics");
  };

  const canManageClients = async (): Promise<boolean> => {
    return await checkPermission("manage_clients");
  };

  const canViewAppointments = async (): Promise<boolean> => {
    return await checkPermission("view_appointments");
  };

  const canManageAppointments = async (): Promise<boolean> => {
    return await checkPermission("manage_appointments");
  };

  const canAccessSupport = async (): Promise<boolean> => {
    return await checkPermission("access_support");
  };

  const canManageSupportTickets = async (): Promise<boolean> => {
    return await checkPermission("manage_support_tickets");
  };

  const canManageUsers = async (): Promise<boolean> => {
    return await checkPermission("manage_users");
  };

  const canManagePackages = async (): Promise<boolean> => {
    return await checkPermission("manage_packages");
  };

  const canManageCategories = async (): Promise<boolean> => {
    return await checkPermission("manage_categories");
  };

  const canManageSystemSettings = async (): Promise<boolean> => {
    return await checkPermission("manage_system_settings");
  };

  const canViewAdminDashboard = async (): Promise<boolean> => {
    return await checkPermission("view_admin_dashboard");
  };

  const canViewAllBusinesses = async (): Promise<boolean> => {
    return await checkPermission("view_all_businesses");
  };

  const canViewAllClients = async (): Promise<boolean> => {
    return await checkPermission("view_all_clients");
  };

  return {
    checkUserPermission,
    canCreateBusiness,
    canViewBusinessMenu,
    canEditBusiness,
    canDeleteBusiness,
    canManageBusinessServices,
    canManageBusinessEmployees,
    canViewBusinessAnalytics,
    canManageClients,
    canViewAppointments,
    canManageAppointments,
    canAccessSupport,
    canManageSupportTickets,
    canManageUsers,
    canManagePackages,
    canManageCategories,
    canManageSystemSettings,
    canViewAdminDashboard,
    canViewAllBusinesses,
    canViewAllClients,
  };
};
